

import api from "../lib/api";
import { validateText, ValidationError } from "../lib/validation";
import { handleError } from "../lib/errors";


export interface Project {
  id: string;
  name: string;
  project_code?: string | null;
  user_id?: string;
  created_at?: string;
  cover_image?: string | null;
  description?: string | null;
    meeting_count?: number;
  last_meeting_date?: string;
  /** Presente apenas para contas colaborador (permissão no projeto). */
  permission?: 'view' | 'edit';
}

// Type para projetos com contagem agregada (usado no Dashboard)
export interface ProjectWithCount extends Project {
  meetingCount: number;
}


interface BackendProjeto {
  id: number;
  nome: string;
  codigo_projeto?: string | null;
  descricao?: string | null;
  imagem_url?: string | null;
  cover_image?: string | null;
  usuarios?: Array<{ id: number; nome: string; email: string }>;
}


function mapBackendToFrontend(backend: BackendProjeto): Project {
  return {
    id: backend.id.toString(),
    name: backend.nome,
    project_code: backend.codigo_projeto || null,
    description: backend.descricao || null,
    cover_image: backend.cover_image || backend.imagem_url || null,
    created_at: undefined,
    user_id: backend.usuarios?.[0]?.id.toString(),
  };
}


function mapFrontendToBackend(
  frontend: Partial<Project>
): Partial<BackendProjeto> {
  const payload: Partial<BackendProjeto> = {};

  if (frontend.name !== undefined) payload.nome = frontend.name;
  if (frontend.project_code !== undefined)
    payload.codigo_projeto = frontend.project_code || null;
  if (frontend.description !== undefined)
    payload.descricao = frontend.description || null;
  if (frontend.cover_image !== undefined)
    payload.cover_image = frontend.cover_image || null;

  return payload;
}


export interface CreateProjectInput {
  name: string;
  project_code?: string;
  description?: string;
  cover_image?: string;
}


export interface UpdateProjectInput {
  name?: string;
  project_code?: string | null;
  description?: string;
  cover_image?: string;
}


// Nova função otimizada com endpoint agregado (elimina N+1)
export async function getAllProjects(): Promise<ProjectWithCount[]> {
  try {
    // Usar novo endpoint com contagem agregada em uma única query
    const response = await api.get("/projetos?include=meeting_counts");

    // Response já vem com meeting_count do backend
    return response.data.map((project: any) => ({
      id: project.id.toString(),
      name: project.nome,
      project_code: project.codigo_projeto || null,
      description: project.descricao || null,
      cover_image: project.cover_image || null,
      meetingCount: project.meeting_count || 0,
      permission: project.permission,
    }));
  } catch (error) {
    const message = handleError(error, "getAllProjects");
    throw new Error(message);
  }
}

// Interface para projetos compartilhados
export interface SharedProject extends ProjectWithCount {
  permission: 'view' | 'edit';
  is_shared: true;
}

// Buscar projetos compartilhados com o usuário
export async function getSharedProjects(): Promise<SharedProject[]> {
  try {
    const response = await api.get("/projetos/shared?include=meeting_counts");

    return response.data.map((project: any) => ({
      id: project.id.toString(),
      name: project.nome,
      project_code: project.codigo_projeto || null,
      description: project.descricao || null,
      cover_image: project.cover_image || null,
      meetingCount: project.meeting_count || 0,
      permission: project.permission,
      is_shared: true,
    }));
  } catch (error) {
    const message = handleError(error, "getSharedProjects");
    throw new Error(message);
  }
}

// Função legada mantida para backward compatibility (usar getAllProjects() em vez desta)
export async function getUserProjects(): Promise<Project[]> {
  try {
    const response = await api.get("/projetos");
    const backendProjetos = Array.isArray(response.data) ? response.data : [];


    const projects = backendProjetos.map(mapBackendToFrontend);



    const projectsWithCounts = await Promise.all(
      projects.map(async (project: Project) => {
        try {
          const numericId = Number(project.id);
          const meetingsResponse = await api.get(`/projetos/${numericId}/atas`);
          const meetings = Array.isArray(meetingsResponse.data)
            ? meetingsResponse.data
            : [];


          const sortedMeetings = meetings.sort((a: any, b: any) => {
            const dateA = new Date(a.data || a.data).getTime();
            const dateB = new Date(b.data || b.data).getTime();
            return dateB - dateA;
          });

          return {
            ...project,
            meeting_count: meetings.length,
            last_meeting_date:
              sortedMeetings.length > 0 && sortedMeetings[0].data
                ? typeof sortedMeetings[0].data === "string"
                  ? sortedMeetings[0].data.split("T")[0]
                  : new Date(sortedMeetings[0].data).toISOString().split("T")[0]
                : undefined,
          };
        } catch {
          return {
            ...project,
            meeting_count: 0,
          };
        }
      })
    );

    return projectsWithCounts;
  } catch (error) {
    const message = handleError(error, "getUserProjects");
    throw new Error(message);
  }
}


export async function getProjectById(projectId: string): Promise<Project> {
  try {

    const numericId = Number(projectId);
    if (isNaN(numericId)) {
      throw new ValidationError("ID do projeto inválido", "projectId");
    }

    const response = await api.get(`/projetos/${numericId}`);
    return mapBackendToFrontend(response.data);
  } catch (error) {
    const message = handleError(error, "getProjectById");
    throw new Error(message);
  }
}


export async function createProject(
  input: CreateProjectInput
): Promise<Project> {
  try {

    const name = validateText(input.name, "Nome do projeto", {
      minLength: 1,
      maxLength: 100,
      required: true,
    });

    const description = input.description
      ? validateText(input.description, "Descrição", {
          maxLength: 500,
          required: false,
          allowEmpty: true,
        })
      : "";

    const project_code = input.project_code
      ? validateText(input.project_code, "Código da obra", {
          maxLength: 50,
          required: false,
          allowEmpty: true,
        })
      : "";

    const payload = mapFrontendToBackend({
      name,
      project_code: project_code || undefined,
      description: description || undefined,
      cover_image: input.cover_image !== undefined ? input.cover_image : undefined,
    });

    const response = await api.post("/projetos", payload);
    return mapBackendToFrontend(response.data);
  } catch (error) {
    const message = handleError(error, "createProject");
    throw new Error(message);
  }
}


export async function updateProject(
  projectId: string,
  input: UpdateProjectInput
): Promise<Project> {
  try {

    const numericId = Number(projectId);
    if (isNaN(numericId)) {
      throw new ValidationError("ID do projeto inválido", "projectId");
    }


    const updates: Partial<Project> = {};

    if (input.name !== undefined) {
      updates.name = validateText(input.name, "Nome do projeto", {
        minLength: 1,
        maxLength: 100,
        required: true,
      });
    }

    if (input.description !== undefined) {
      updates.description = validateText(input.description, "Descrição", {
        maxLength: 500,
        required: false,
        allowEmpty: true,
      });
    }

    if (input.project_code !== undefined) {
      updates.project_code = input.project_code === null
        ? null
        : validateText(input.project_code, "Código da obra", {
            maxLength: 50,
            required: false,
            allowEmpty: true,
          });
    }


    if (
      input.cover_image !== undefined &&
      input.cover_image &&
      typeof input.cover_image !== "string"
    ) {
      throw new ValidationError("Imagem de capa inválida", "cover_image");
    }


    if (input.cover_image !== undefined) {
      updates.cover_image = input.cover_image;
    }


    if (Object.keys(updates).length === 0) {
      throw new ValidationError("Nenhum campo válido para atualizar. Verifique se os dados foram informados corretamente.");
    }

    const payload = mapFrontendToBackend(updates);

    const response = await api.put(`/projetos/${numericId}`, payload);
    
    const mappedProject = mapBackendToFrontend(response.data);
    
    return mappedProject;
  } catch (error) {
    const message = handleError(error, "updateProject");
    throw new Error(message);
  }
}


export async function deleteProject(projectId: string): Promise<void> {
  try {

    const numericId = Number(projectId);
    if (isNaN(numericId)) {
      throw new ValidationError("ID do projeto inválido", "projectId");
    }
    await api.delete(`/projetos/${numericId}`);
  } catch (error) {
    const message = handleError(error, "deleteProject");
    throw new Error(message);
  }
}


export async function changeProjectCover(
  projectId: string,
  coverImage: string
): Promise<Project> {
  return updateProject(projectId, { cover_image: coverImage });
}
